import {injectable} from "inversify";
import config from "../config";
import ScriptLoader from "./Utilities/script-loader";

export type AppcuesBaseProps = {
    env: string | null;
}

export interface IAppcuesIdentity {
    shop_id?: number;
    shop_name?: string;
    human_id?: number;
    human_email?: string;
}

export type AppcuesOptionalProperties = Record<string, any>;

interface IAppcuesClient {
    identify: (id: string, identify: Record<string, any>) => void;
    track: (event: string, properties: Record<string, any>) => void;
}

@injectable()
class AppcuesService {
    private appcuesClientSetupFinished: boolean = false;
    private appcuesClient: IAppcuesClient | undefined;

    private get isProductionEnv(): boolean {
        return config.env === 'app';
    }

    /**
     * Initializes the Appcues client by dynamically loading the necessary script
     * and setting it up with the given user identity. It also checks various
     * configuration conditions to decide whether to proceed with initialization.
     *
     * @param identity - An object containing the identity details of the shop and user.
     *                   Includes information such as shop ID, shop name, human ID, and human email.
     * @returns A promise that resolves to a boolean indicating whether the setup was successful.
     */
    public async setupAppcuesClient(identity: IAppcuesIdentity): Promise<void> {

        /** do not run in production environment */
        if (this.appcuesClientSetupFinished || this.isProductionEnv) {
            return;
        }

        /** Verify that the Appcues Account ID is present in the configuration. */
        if (!config.appcuesAccountId) {
            console.info('[AppcuesService.setupAppcuesClient] Missing Appcues key - appCues is not initialized');
            return;
        }

        try {
            /**
             * Dynamically load the Appcues script by appending it to the document's <head>.
             * This also ensures that Appcues is initialized and available for use.
             */
            if (!this.appcuesClient) {
                /** Create Appcues settings script element */
                /** Configure Appcues settings dynamically (e.g., enabling URL detection) */
                window.AppcuesSettings = { enableURLDetection: true };

                await ScriptLoader.loadScript(`//fast.appcues.com/${config.appcuesAccountId}.js`);
                this.appcuesClient = window.Appcues;
                this.appcuesClientSetupFinished = true;
                this.setUserIdentity(identity);
            }

        } catch (error) {
            /** Log an error if the initialization of Appcues fails */
            console.error('[AppcuesService.setupAppcuesClient] failed:', error);
        }
    }

    private setUserIdentity(identity: IAppcuesIdentity) {
        if(!this.appcuesClientSetupFinished) {
            console.warn('[AppcuesService.setUserIdentity] Appcues client is not initialized');
            return;
        }

        this.appcuesClient?.identify(`${identity.human_id}|${identity.shop_id}|${identity.shop_name}`,
            {
                email: identity.human_email,
                name: `${identity.shop_id}|${identity.shop_name}`
            }
        );
    }

    /**
     * Tracks an event using the Appcues client if it is initialized.
     *
     * This method:
     * 1. Returns immediately if Appcues functionality is turned off via configuration.
     * 2. Combines the provided properties with base properties such as the environment and sends them to Appcues.
     *
     * @param event - The name of the event to track.
     * @param properties - Optional properties to be sent along with the event.
     *
     * @returns A Promise that resolves once the tracking logic has completed.
     */
    public async trackEvent(event: string, properties: AppcuesOptionalProperties): Promise<void> {

        if (!this.appcuesClientSetupFinished) {
            console.info('[AppcuesService::trackEvent] Appcues client is not initialized', event, JSON.parse(JSON.stringify(properties)));
            return;
        }

        try {
            const allProperties: AppcuesBaseProps & AppcuesOptionalProperties = {
                env: config.env ?? null,
                ...(properties || {}),
            };

            this.appcuesClient?.track(event, allProperties);
        } catch (error) {
            console.error('[AppcuesService::trackEvent] failed:', error);
        }
    }
}

export default AppcuesService;
